import React from 'react'
import { cn } from '@/lib/utils'

interface Option {
    id: string
    text: string
}

interface RadioProps {
    questionId: string
    options: Option[]
    allowOther?: boolean
    otherLabel?: string
    displayType?: 'radio' | 'pill'
    selectedOptionId?: string
    otherValue?: string
}

const Radio: React.FC<RadioProps> = ({
    questionId,
    options,
    allowOther = false,
    otherLabel = 'Other',
    displayType = 'radio',
    selectedOptionId,
    otherValue = '',
}) => {
    if (displayType === 'pill') {
        return (
            <div
                className={`w-full flex flex-wrap gap-2 mt-4`}
                key={`${questionId}-radio-pill`}
            >
                {options.map(
                    (option: { id: string; text: string }, index: number) => (
                        <div
                            key={option.id}
                            id={`${questionId}-${option.id}`}
                            className={cn(
                                'h-[36px] rounded-md px-3 text-[12px] font-[500] transition-all duration-150 shadow-none flex items-center',
                                selectedOptionId === option.id
                                    ? 'border-[#005BD3] bg-[#005BD3]/10 border'
                                    : 'border-[#D9D9D9] border'
                            )}
                        >
                            {option?.text !== ''
                                ? option.text
                                : `Response ${index + 1}`}
                        </div>
                    )
                )}
                {allowOther && (
                    <div
                        id={`${questionId}-other`}
                        className={cn(
                            'h-[36px] rounded-md px-3 text-[12px] font-[500] transition-all duration-150 shadow-none flex items-center',
                            selectedOptionId === 'other'
                                ? 'border-[#005BD3] bg-[#005BD3]/10 border'
                                : 'border-[#D9D9D9] border'
                        )}
                    >
                        {otherLabel}
                    </div>
                )}
                {selectedOptionId === 'other' && (
                    <input
                        type="text"
                        key={`${questionId}-other-input`}
                        value={otherValue}
                        disabled
                        className={cn(
                            'mt-2 w-full outline-none rounded-md placeholder-[#616161] border border-[0.5px] border-[#8A8A8A] bg-background px-2 py-1.5 text-xs shadow-none',
                            otherValue !== '' ? 'font-[550]' : 'font-[400]'
                        )}
                        placeholder="Please specify"
                        style={{ maxWidth: 300 }}
                    />
                )}
            </div>
        )
    }

    return (
        <div className="relative radio-group mt-4 flex flex-col gap-[14px]">
            {options.map((option, index) => (
                <div
                    key={`${questionId}-${option.id}-modal`}
                    className="radio-option"
                >
                    <RadioButton
                        id={`${questionId}-${option.id}`}
                        checked={selectedOptionId === option.id}
                        label={
                            option?.text !== ''
                                ? option.text
                                : `Response ${index + 1}`
                        }
                    />
                </div>
            ))}
            {allowOther && (
                <div className="relative radio-option">
                    <RadioButton
                        id={`${questionId}-other`}
                        checked={selectedOptionId === 'other'}
                        label={otherLabel}
                    />
                    {selectedOptionId === 'other' && (
                        <input
                            type="text"
                            value={otherValue}
                            disabled
                            className={cn(
                                'mt-2 ml-7 w-[414px] outline-none rounded-md placeholder-[#616161]  border border-[0.5px] border-[#8A8A8A] bg-background px-3 py-2 text-sm shadow-none',
                                otherValue !== '' ? 'font-[550]' : 'font-[400]'
                            )}
                            placeholder="Please specify"
                        />
                    )}
                </div>
            )}
        </div>
    )
}

interface RadioButtonProps {
    id: string
    checked: boolean
    label: string
}

const RadioButton: React.FC<RadioButtonProps> = ({ id, checked, label }) => {
    return (
        <label
            key={`${id}-preview`}
            htmlFor={id + 'preview'}
            className="flex items-center text-[14px] font-[400] select-none gap-[11px]"
        >
            <div className="relative flex-shrink-0 w-[18px] h-[18px]">
                <input
                    type="radio"
                    id={id + 'preview'}
                    key={`${id}-preview-input`}
                    checked={checked}
                    disabled
                    className="sr-only"
                />
                <span
                    className={`inline-block w-[18px] h-[18px] rounded-full transition-colors ${
                        checked ? 'bg-[#015AD1]' : 'border border-[#DEDEDE]'
                    }`}
                >
                    {checked && (
                        <span className="block w-[6px] h-[6px] rounded-full bg-white mx-auto my-[6px]"></span>
                    )}
                </span>
            </div>
            <span>{label}</span>
        </label>
    )
}

export default Radio
